import React, { useMemo } from "react";
import {
  getPrimaryCartDiscountCode,
  hasAppliedCartDiscountCode,
} from "../../../shared/cart-discount-codes.js";
import { getCartDiscountDisplayRows } from "../../../shared/cart-discount-display.js";

/** Solid circle + optically centered check (even padding). */
const CheckBadgeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
    <circle cx="14" cy="14" r="12" fill="#111827" />
    <path
      d="M8.9 14.6l3.1 3.1 7.1-7.1"
      stroke="#fff"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Matching circle badge for available coupons. */
const PercentBadgeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
    <circle cx="14" cy="14" r="12" fill="#111827" />
    <circle cx="10.4" cy="10.4" r="1.55" fill="#fff" />
    <circle cx="17.6" cy="17.6" r="1.55" fill="#fff" />
    <path
      d="M18.4 9.6L9.6 18.4"
      stroke="#fff"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Checkout-style discount summary row — opens Save more sheet.
 * Shows applied savings + available coupon count when applicable.
 */
export default function CartSaveMoreSummaryRow({
  cart,
  formatMoney,
  couponCount = 0,
  discountCodeLabels = {},
  onOpen,
  disabled = false,
  editorPartProps = {},
}) {
  const appliedCode = useMemo(() => getPrimaryCartDiscountCode(cart), [cart]);
  const hasApplied = hasAppliedCartDiscountCode(cart);

  const { rows, totalDiscountCents } = useMemo(
    () =>
      getCartDiscountDisplayRows(cart, {
        formatMoney,
        discountCodeLabels,
      }),
    [cart, formatMoney, discountCodeLabels]
  );

  const savingsCents = Math.max(
    0,
    totalDiscountCents || Number(cart?.total_discount) || 0
  );
  const codeLabel =
    rows[0]?.title ||
    appliedCode ||
    (discountCodeLabels && appliedCode
      ? discountCodeLabels[String(appliedCode).toLowerCase()]
      : "") ||
    appliedCode;

  const primaryText =
    hasApplied && savingsCents > 0 && typeof formatMoney === "function"
      ? `Save ${formatMoney(savingsCents)} with `
      : hasApplied && codeLabel
        ? `${codeLabel} applied`
        : couponCount > 0
          ? "Save more with coupons and offers"
          : "Add discount code";

  const showCodeInTitle =
    hasApplied && savingsCents > 0 && typeof formatMoney === "function" && codeLabel;

  const secondaryText =
    couponCount > 0
      ? "Coupons and offers"
      : hasApplied
        ? "View more offers"
        : null;

  return (
    <button
      type="button"
      className={`weu-custom-cart-drawer__save-more-row weu-custom-cart-drawer__part${
        hasApplied ? " weu-custom-cart-drawer__save-more-row--applied" : ""
      }`}
      disabled={disabled}
      aria-label={
        showCodeInTitle
          ? `${primaryText}${codeLabel}. ${secondaryText || ""}`.trim()
          : secondaryText
            ? `${primaryText}. ${secondaryText}`
            : primaryText
      }
      {...editorPartProps}
      onClick={(event) => {
        editorPartProps?.onClick?.(event);
        if (event?.defaultPrevented) return;
        onOpen?.(event);
      }}
      onKeyDown={(event) => {
        editorPartProps?.onKeyDown?.(event);
      }}
      style={undefined}
    >
      <span className="weu-custom-cart-drawer__save-more-row-icon">
        {hasApplied ? <CheckBadgeIcon /> : <PercentBadgeIcon />}
      </span>
      <span className="weu-custom-cart-drawer__save-more-row-copy">
        <span className="weu-custom-cart-drawer__save-more-row-title">
          {showCodeInTitle ? (
            <>
              {primaryText}
              <strong>{codeLabel}</strong>
            </>
          ) : (
            primaryText
          )}
        </span>
        {secondaryText ? (
          <span className="weu-custom-cart-drawer__save-more-row-sub">
            {secondaryText} ›
          </span>
        ) : null}
      </span>
      <span className="weu-custom-cart-drawer__save-more-row-apply" aria-hidden>
        Apply
      </span>
    </button>
  );
}
